import { css, html, htmlArray } from "../helpers/webComponents";
import { GameRouteService } from "../services/GameRouteService";

/** CSS affecting the {@link PlayerLeaderboardComponent} */
const styles: string = css`
:host {
    height: 90vh;
    width: 100%;
    margin-top: 10%;
    border-radius: 12px;
    padding: 10px;
    background: rgba(41, 35, 35, 0.85);
    border: 1px solid rgba(255, 255, 255, 0.2);
    box-sizing: border-box;
}

.leaderboard-component {
    height: 100%;
}

.players {
    height: 92.5%;
    border-radius: 12px;
    display: grid;
    grid-template-columns: 1fr;
    gap: 9px;
    padding: 20px;
    margin: 0 auto;
    overflow: auto;
    overflow-x: hidden;
}

.title {
    font-size: 1.5rem;
    font-weight: bold;
    color: #f5f5f5;
    text-align: center;
    margin-bottom: 10px;
}

.player-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.15);
    border-radius: 10px;
    padding: 4px;
    text-align: center;
    color: #fff;
    transition: transform 0.2s ease, box-shadow 0.3s ease, background-color 0.3s ease;
    border: 1px solid rgba(255, 255, 255, 0.25);
}

.player-item:hover {
    transform: scale(1.05);
    box-shadow: 0 4px 15px rgba(255, 255, 255, 0.2);
    background-color: rgba(255, 255, 255, 0.2);
}

.player-item .name {
    margin-top: 8px;
    font-size: 1.2rem;
    font-weight: bold;
    color: #f5f5f5;
    text-transform: uppercase;
}

.player-item .score {
    font-size: 1.2rem;
    color: #f5f5f5;
    margin-top: 8px;
    font-weight: 600;
}

.no-players {
    text-align: center;
    color: #fff;
    font-size: 1.3rem;
    font-weight: bold;
    padding: 20px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    box-shadow: 0 4px 8px rgba(255, 255, 255, 0.1);
}

.players::-webkit-scrollbar {
    width: 8px;
}

.players::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 10px;
}

.players::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 10px;
}

.players::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.5);
}

`;

/**
 * Represents the leaderboard
 */
export class LeaderboardComponent extends HTMLElement {
    /** Instance of the game route service */
    private readonly _gameRouteService: GameRouteService = new GameRouteService();

    private _players: { username: string; duration_seconds: number }[] = [];

    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });

        void this.render();
    }

    public set players(players: { username: string; duration_seconds: number }[]) {
        this._players = players;
    }

    /**
    * Render the contents of this page
    */
    public async render(): Promise<void> {
        if (!this.shadowRoot) return;

        await this.loadLeaderboard();

        const element: HTMLElement = this.renderLeaderboard();

        this.shadowRoot.innerHTML = "";
        this.shadowRoot.append(element);
    }

    /**
     * Load player leaderboard data from the game service
     */
    private async loadLeaderboard(): Promise<void> {
        try {
            const leaderboard: { username: string; duration_seconds: number }[] = await this._gameRouteService.getLeaderboard();
            console.log("Leaderboard data:", leaderboard);

            this._players = leaderboard;
        }
        catch (error) {
            console.error("Failed to load leaderboard:", error);
        }
    }

    /**
     * Render the leaderboard container
     */
    private renderLeaderboard(): HTMLElement {
        return html`
        <div class="leaderboard-component">
            <style>${styles}</style>
            <div class="players">
                <div class="title">HighScores</div>
                ${this._players.length === 0 ? this.renderNoPlayers() : this.renderPlayers()}
            </div>
        </div>`;
    }

    /**
     * Render the player items
     */
    private renderPlayers(): HTMLElement[] {
        return htmlArray`
            ${this._players.map(player => html`
                <div class="player-item">
                    <div class="name">${player.username}</div>
                    <div class="score">${player.duration_seconds}</div>
                </div>
            `)}
        `;
    }

    /**
     * Render message when no players are available
     */
    private renderNoPlayers(): HTMLElement {
        return html`<div class="no-players">No players data available</div>`;
    }
}
